import React, { useState, useEffect } from "react";
import { uploadFileToS3Complete } from "../../api/s3";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { updateDraftItem, extractFromAudio, getProjects } from "../../api";
import AudioPlayerRewind from "../../components/common/AudioPlayer";
import Dropdown from "../../components/common/Dropdown";
import { FileInput } from "../../components/common/FileInput";
import TextInput from "../../components/ui/TextInput";
import { useGlobalState } from "../../context/useGlobalState";
import { AudioAI } from "../../types/types";
import Icon from "../../components/common/Icon";
import e from "../projects/Edition.module.css";

interface FieldErrors {
    selectedContentLanguage: boolean;
    singleTitle: boolean;
}

/**
 * Rich edition for an AUDIO draft (item not assigned to a project). Mirrors the
 * project item edition (ModifyContentProject) but saves through the draft API.
 * The project selector additionally offers "No project" (keep it as a draft).
 * The item is passed through navigation state (like TextDraftEdit).
 */
const AudioDraftEdit: React.FC = () => {
    const { itemId } = useParams<{ itemId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { data, loading, error } = useGlobalState();

    const stateItem = (location.state as { item?: AudioAI } | null)?.item;
    const listPath = "/create/drafts";

    const [selectedContentLanguage, setSelectedContentLanguage] = useState<string | null>(null);
    const [singleTitle, setSingleTitle] = useState<string>("");
    const [singleText, setSingleText] = useState<string>("");
    const [mp3File, setMp3File] = useState<string | null>(null);
    // Voice is read-only at edition (info only — re-voicing would require regeneration).
    const [voiceName, setVoiceName] = useState<string>("");
    // Project assignment: "" = keep as a draft (no project) ; an id = assign to that project.
    const [projectOptions, setProjectOptions] = useState<{ value: string; label: string }[]>([]);
    const [selectedProjectId, setSelectedProjectId] = useState<string>("");

    const [showErrors, setShowErrors] = useState<boolean>(false);
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({
        selectedContentLanguage: false,
        singleTitle: false,
    });
    const [isGenerateButtonDisabled, setIsGenerateButtonDisabled] = useState<boolean>(true);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [activeTab, setActiveTab] = useState<"text" | "audio">("text");

    const wordCount = singleText.trim() ? singleText.trim().split(/\s+/).length : 0;
    const charCount = singleText.length;

    // Without navigation state we cannot resolve the item; return to the list.
    useEffect(() => {
        if (!stateItem) navigate(listPath);
    }, [stateItem, navigate]);

    // Hydrate the form from the passed draft item.
    useEffect(() => {
        if (stateItem && data.languagesAI && data.languagesAI.length > 0) {
            setSingleTitle(stateItem.name || "");
            setSingleText(stateItem.text || "");
            const findLanguage = data.languagesAI.find((lang) => lang.code === stateItem.language);
            setSelectedContentLanguage(findLanguage?.value || "");
            setMp3File(stateItem.audioUrl || null);
            const rawItem = stateItem as any;
            setVoiceName(rawItem?.voiceAI?.name || "");
        }
    }, [stateItem, data.languagesAI]);

    // Load the company's audio projects for the "Project" dropdown.
    useEffect(() => {
        const fetchProjectOptions = async () => {
            const token = localStorage.getItem("token");
            if (!token) return;
            try {
                const projects = await getProjects({ token, itemType: "audio" });
                setProjectOptions(
                    (projects || []).map((p: { id: string; name: string }) => ({ value: p.id, label: p.name })),
                );
            } catch (err) {
                console.error("Failed to fetch projects:", err);
            }
        };
        fetchProjectOptions();
    }, []);

    const handleSingleFileSelected = (files: File | File[]) => {
        const file = Array.isArray(files) ? files[0] : files;
        if (!file) return;
        const token = localStorage.getItem("token");
        if (!token) {
            console.error("No authentication token found");
            return;
        }
        uploadFileToS3Complete({ file, isPublic: true, isFolder: false }, token)
            .then((result: string) => setMp3File(result))
            .catch((err) => console.log(err));
    };

    useEffect(() => {
        const errors: FieldErrors = {
            selectedContentLanguage: !selectedContentLanguage,
            singleTitle: !singleTitle.trim(),
        };
        setFieldErrors(errors);
        setIsGenerateButtonDisabled(Object.values(errors).some((err) => err));
    }, [selectedContentLanguage, singleTitle, singleText, mp3File]);

    const showErrorMessage = () => {
        setShowErrors(true);
        setTimeout(() => setShowErrors(false), 3000);
    };

    const downloadAudio = async () => {
        if (!mp3File) return;
        try {
            const response = await fetch(mp3File, { cache: "reload" });
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `${singleTitle || "audio"}.mp3`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error("Error downloading audio:", err);
        }
    };

    const saveItem = async () => {
        setIsLoading(true);
        if (!singleTitle.trim() && !singleText.trim() && !selectedContentLanguage && !mp3File) {
            setIsLoading(false);
            showErrorMessage();
            return;
        }
        try {
            const token = localStorage.getItem("token");
            if (!token || !itemId) return;
            const languageCode = data.languagesAI.find((lang) => lang.value === selectedContentLanguage)?.code;
            if (!languageCode) {
                setIsLoading(false);
                return;
            }
            // "" (No project) keeps it a draft; a real id assigns it to that project.
            const targetProjectAIId = selectedProjectId || undefined;
            const resp = await updateDraftItem({
                token,
                itemId,
                itemType: "audio",
                language: languageCode,
                title: singleTitle,
                text: singleText,
                audioUrl: mp3File || undefined,
                targetProjectAIId,
            });
            if (resp && resp.message) {
                // Assigned to a project → follow it there; otherwise back to the drafts list.
                if (targetProjectAIId) navigate(`/projects/${targetProjectAIId}`);
                else navigate(listPath);
            }
            setIsLoading(false);
        } catch (err) {
            console.error("Failed to save draft item:", err);
            setIsLoading(false);
        }
    };

    const audioAIExtract = async () => {
        setIsLoading(true);
        if (!mp3File) {
            setIsLoading(false);
            return;
        }
        const token = localStorage.getItem("token");
        if (!token || !itemId) return;
        const resp = await extractFromAudio({ token, audioFile: mp3File, audioAIId: itemId });
        if (resp && resp.transcription) {
            setSingleText(resp.transcription);
        }
        setIsLoading(false);
    };

    if (!stateItem) {
        return <div className={e.page}>{t("loading")}...</div>;
    }

    const langCode = data.languagesAI.find((lang) => lang.value === selectedContentLanguage)?.code || "";

    return (
        <div className={e.page}>
            <button type="button" className={e.breadcrumb} onClick={() => navigate(listPath)}>
                <Icon name="ic-arrow-right" className={e.breadcrumbIcon} />
                {t("drafts")}
            </button>

            <div className={e.header}>
                <div className={e.titleRow}>
                    <h1 className={e.title}>{singleTitle || t("whatIsTheTitleOfYourStory")}</h1>
                    {langCode && <span className={e.badge}>{langCode.toUpperCase()}</span>}
                </div>
                <div className={e.headerActions}>
                    {mp3File && (
                        <button type="button" className={e.ghostBtn} onClick={downloadAudio}>
                            <Icon name="ic-download" className={e.ghostBtnIcon} />
                            {t("download")}
                        </button>
                    )}
                    <button
                        type="button"
                        className={e.primaryBtn}
                        onClick={saveItem}
                        disabled={isGenerateButtonDisabled || isLoading}
                    >
                        {isLoading ? t("processing") : t("save")}
                    </button>
                </div>
            </div>

            {/* Language / Voice (read-only) / Project (No project = keep as draft) */}
            <div className={e.metaCard}>
                <div className={e.field}>
                    <p className={e.fieldLabel}>{t("language")}</p>
                    <Dropdown
                        label=""
                        options={data.languagesAI}
                        onChange={setSelectedContentLanguage}
                        loading={loading}
                        error={error ? true : false}
                        placeholder={t("language")}
                        value={selectedContentLanguage}
                        isMissing={showErrors && fieldErrors.selectedContentLanguage}
                        fullWidth
                        isWithoutMargin
                    />
                </div>
                <div className={e.field}>
                    <p className={e.fieldLabel}>{t("voice")}</p>
                    <div className={e.readonlyValue}>{voiceName || "—"}</div>
                </div>
                <div className={e.field}>
                    <p className={e.fieldLabel}>{t("project")}</p>
                    <Dropdown
                        label=""
                        options={projectOptions}
                        onChange={(val) => setSelectedProjectId(val || "")}
                        loading={false}
                        error={false}
                        placeholder={t("noProject")}
                        value={selectedProjectId}
                        fullWidth
                        isWithoutMargin
                    />
                </div>
            </div>

            {/* Tabs */}
            <div className={e.tabs}>
                <button
                    className={`${e.tab} ${activeTab === "text" ? e.tabActive : ""}`}
                    onClick={() => setActiveTab("text")}
                >
                    {t("tabText")}
                </button>
                <button
                    className={`${e.tab} ${activeTab === "audio" ? e.tabActive : ""}`}
                    onClick={() => setActiveTab("audio")}
                >
                    {t("tabAudio")}
                </button>
            </div>

            {activeTab === "text" ? (
                <div className={e.panel}>
                    <div className={e.titleField}>
                        <p className={e.fieldLabel}>{t("whatIsTheTitleOfYourStory")}</p>
                        <TextInput
                            placeholder={t("pleaseInsertYourText")}
                            value={singleTitle}
                            onChange={(ev) => setSingleTitle(ev.target.value)}
                            size={"medium"}
                            isMissing={showErrors}
                        />
                    </div>
                    <div className={e.panelHeader}>
                        <div>
                            <h3 className={e.panelTitle}>{t("textEdition")}</h3>
                            <p className={e.panelSubtitle}>{t("textEditionSub")}</p>
                        </div>
                        {mp3File && (
                            <button className={e.extractBtn} disabled={isLoading} onClick={audioAIExtract}>
                                {isLoading ? t("processing") : t("extractFromAudio")}
                            </button>
                        )}
                    </div>
                    <TextInput
                        customClassName={e.textarea}
                        placeholder={t("pleaseInsertYourText")}
                        value={singleText}
                        onChange={(ev) => setSingleText(ev.target.value)}
                        size="big"
                        numberOfLines={12}
                    />
                    <div className={e.panelFooter}>
                        <span>
                            {wordCount} {t("wordsLabel")} · {charCount} {t("charactersLabel")}
                        </span>
                    </div>
                </div>
            ) : (
                <div className={e.panel}>
                    <div className={e.panelHeader}>
                        <div>
                            <h3 className={e.panelTitle}>{t("audioEdition")}</h3>
                            <p className={e.panelSubtitle}>{t("addAudioManually")}</p>
                        </div>
                    </div>
                    <FileInput
                        onFileSelected={handleSingleFileSelected}
                        placeholder={t("dragAndDropOrClick")}
                        isMissing={showErrors}
                    />
                    {mp3File && (
                        <div style={{ marginTop: 18 }}>
                            <AudioPlayerRewind
                                title={singleTitle}
                                src={mp3File}
                                isAutoPlay={false}
                                onClickAddToProject={() => {}}
                                showAddtoProject={false}
                            />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default AudioDraftEdit;
